import React, { useState, useEffect } from "react";
import {
  Container,
  LeftSection,
  CenterSection,
  RightSection,
  FilterButton,
  DateDisplay,
  CompanyStatus,
  StatusItem,
  ThemeToggle,
  SimulationButton,
  SimulationIndicator,
  StopSimButton,
  TrialBadge,
} from "./styles";
import { FiFilter, FiSun, FiMoon, FiCalendar, FiFile, FiCode, FiEye, FiXCircle, FiClock, FiAlertTriangle, FiZap } from "react-icons/fi";
import useSubscription from "../../hooks/useSubscription";
import { GoOrganization } from "react-icons/go";
import FilterModal from "./FilterModal";
import ProjectSelectorModal from "../ProjectSelectorModal";
import UserSimulationModal from "../UserSimulationModal";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import simulationService from "../../services/simulationService";
import Tooltip from "../Tooltip";

export default function StatusBar({ isDark = true, onToggleTheme }) {
  const { user, updateProjectData, exitSimulation } = useAuth();
  const { isTrial, isExpired, isPastDue, trialDaysLeft, planName, isGod: subIsGod } = useSubscription();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [stats, setStats] = useState({ certificates: 0, xmls: 0, speds: 0 });
  const [isSimulationModalOpen, setIsSimulationModalOpen] = useState(false);

  // O usuário real é GOD se: cargo é god OU se há simulação ativa (só GOD inicia simulação)
  const isReallyGod = user?.cargo === 'god' || simulationService.isSimulating();
  const isSimulating = simulationService.isSimulating();

  // Mock data - TODO: Buscar dados reais do backend
  // empresas = número de certificados do projeto
  // xmls = quantidade de XMLs na pasta SERVER_FILES/projeto/xmls
  // arquivos = quantidade de SPEDs na pasta SERVER_FILES/projeto/speds
  const mockData = {
    startDate: "01/09/2019",
    endDate: "01/12/2024",
    empresa: "Empresa ABC Ltda",
    empresas: 6,
    xmls: 77000,
    arquivos: 156,
  };

  const formatNumber = (num) => num.toLocaleString("pt-BR");

  const fetchProjectData = async (projectId) => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/projects/${projectId}`);

      if (response.data) {
        const project = response.data;
        const projectInfo = {
          id: project.id || projectId,
          nome: project.nome || 'Empresa',
          cnpj: project.cnpj,
          // Bloco A (Dados do Cliente) do simulador de Reforma Tributária —
          // sem isso aqui, esses campos são descartados mesmo que o backend
          // já os retorne, e a tela continua mostrando texto fixo de exemplo.
          regime: project.regime || '',
          filiais: project.filiais || '',
          atividade: project.atividade || '',
          color: project.color,
          empresas: project.empresas,
          nfes: project.nfes,
          speds: project.speds,
          permissions: project.permissions
        };
        
        setProjectData(projectInfo);
        
        // Atualizar contexto de autenticação com dados do projeto
        updateProjectData(projectInfo);
      }
    } catch (error) {
      console.error('Erro ao buscar dados do projeto:', error);
      setProjectData(null);
      updateProjectData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await api.get('/projects/user/my-projects');

      if (response.data?.projects) {
        setUserProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Erro ao buscar projetos do usuário:', error);
      setUserProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchProjectStats = async (projectId) => {
    if (!projectId) return;
    
    try {
      const response = await api.get(`/projects/${projectId}/realtime-stats`);
      
      if (response.data?.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas do projeto:', error);
      setStats({ certificates: 0, xmls: 0, speds: 0 });
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserProjects();
      const currentProjectId = localStorage.getItem('vidal_selected_project') || user?.projetos?.[0];
      if (currentProjectId) {
        fetchProjectData(currentProjectId);
        fetchProjectStats(currentProjectId);
      }
    }
  }, [user]);

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = (filters) => {
    // Filters applied
    // TODO: Implementar lógica de filtro
  };

  const handleOpenProjectModal = () => {
    fetchUserProjects();
    setIsProjectModalOpen(true);
  };

  const handleSelectProject = (projectId) => {
    // A lógica de salvar e recarregar está no modal
    // Project selected
  };

  const currentProjectId = localStorage.getItem('vidal_selected_project') || user?.projetos?.[0];

  return (
    <>
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleApplyFilters}
      />
      <ProjectSelectorModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        projects={userProjects}
        currentProjectId={currentProjectId}
        onSelectProject={handleSelectProject}
        loading={loadingProjects}
      />
      {isReallyGod && (
        <UserSimulationModal
          isOpen={isSimulationModalOpen}
          onClose={() => setIsSimulationModalOpen(false)}
        />
      )}
      <Container data-tour="statusbar">
        {/* Temporariamente comentado - será reativado depois */}
        {/* <LeftSection>
          <FilterButton onClick={handleFilter}>
            <FiFilter />
            <span>Filtros</span>
          </FilterButton>
        </LeftSection> */}

      <CenterSection>
        {/* Temporariamente comentado - será reativado depois */}
        {/* <DateDisplay>
          <FiCalendar />
          <span>{mockData.startDate}</span>
        </DateDisplay>

        <span className="separator">até</span>

        <DateDisplay>
          <FiCalendar />
          <span>{mockData.endDate}</span>
        </DateDisplay> */}

        <Tooltip content="Empresa selecionada. Clique para trocar de projeto." placement="bottom">
          <CompanyStatus onClick={handleOpenProjectModal}>
            <GoOrganization />
            <span>
              {loading ? 'Carregando...' : (projectData?.nome || mockData.empresa)}
            </span>
          </CompanyStatus>
        </Tooltip>
      </CenterSection>

      <RightSection>
        <Tooltip content="Certificados digitais do projeto" placement="bottom">
          <StatusItem>
            <GoOrganization />
            <span>{formatNumber(stats.certificates)}</span>
          </StatusItem>
        </Tooltip>

        <Tooltip content="Quantidade de XMLs (NFes, CTes) do projeto" placement="bottom">
          <StatusItem>
            <FiCode />
            <span>{formatNumber(stats.xmls)}</span>
          </StatusItem>
        </Tooltip>

        <Tooltip content="Quantidade de SPEDs processados do projeto" placement="bottom">
          <StatusItem>
            <FiFile />
            <span>{formatNumber(stats.speds)}</span>
          </StatusItem>
        </Tooltip>

        {isReallyGod && !isSimulating && (
          <Tooltip content="Emular outro usuário (apenas God)" placement="bottom">
            <SimulationButton onClick={() => setIsSimulationModalOpen(true)}>
              <FiEye />
              <span>Emular</span>
            </SimulationButton>
          </Tooltip>
        )}

        {isSimulating && (() => {
          const simData = simulationService.getLocalSimulation();
          return (
            <SimulationIndicator>
              <FiEye />
              <span>{simData?.name || 'Usuário'}</span>
              <Tooltip content="Parar emulação" placement="bottom">
                <StopSimButton onClick={async () => {
                  await exitSimulation();
                }}>
                  <FiXCircle />
                </StopSimButton>
              </Tooltip>
            </SimulationIndicator>
          );
        })()}

        {/* Trial / Subscription Badge — controlado pelo Mercado Pago */}

        <Tooltip content={isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'} placement="bottom">
          <ThemeToggle $isDark={isDark}>
            <FiSun />
            <div className="toggle" onClick={onToggleTheme}>
              <div className="circle" />
            </div>
            <FiMoon />
          </ThemeToggle>
        </Tooltip>
      </RightSection>
    </Container>
    </>
  );
}
